
import {
  FinancialInstitution,
  GetFinancialInstitutionResponse,
  OriginationUserProfile,
  SearchFinancialInstitutionsResponse,
  UpdateProfileResponse,
} from '@ld.consumer-portal/models';
import { EndpointScenarioMap } from './mock.types';

interface CheckRegistrationResponse {
  customerGuid: string;
  loanId?: string;
  username?: string;
  isActive: boolean;
}

interface ConfirmRegistrationResponse {
  userGuid?: string;
  activationToken?: string;
  createPasswordToken?: string;
  authToken?: string;
  issuedAt?: Date;
  expiresAt?: Date;
  expiresInSeconds?: number;
  email?: string;
  loanId?: string;
  borrowerId?: string;
  firstName?: string;
  lastName?: string;
}

interface B2CFeatureFlagResponse {
  enabled: boolean;
  partnerId?: string;
}

interface UserLoan {
  loanId: string;
  loanNumber?: string;
  status?: string;
  loanType?: string;
  propertyAddress?: string;
}

export const checkRegistrationScenarios: EndpointScenarioMap<CheckRegistrationResponse> = {
  default: {
    customerGuid: 'cust-guid-001',
    loanId: 'loan-001',
    username: 'john.smith@email.com',
    isActive: true,
  },
  'needs-confirmation': {
    customerGuid: 'cust-guid-002',
    loanId: 'loan-002',
    isActive: false,
  },
  expired: {
    customerGuid: '',
    isActive: false,
  },
};

export const confirmRegistrationScenarios: EndpointScenarioMap<ConfirmRegistrationResponse> = {
  default: {
    userGuid: 'mock-user-guid-001',
    activationToken: 'mock-activation-token-abc',
    createPasswordToken: 'token-abc123',
    authToken: 'auth-token-xyz789',
    issuedAt: new Date(),
    expiresAt: new Date(Date.now() + 60 * 60 * 1000),
    expiresInSeconds: 3600,
    email: 'john.smith@email.com',
    loanId: 'loan-001',
    borrowerId: 'borrower-001',
    firstName: 'John',
    lastName: 'Smith',
  },
  success: {
    userGuid: 'mock-user-guid-001',
    activationToken: 'mock-activation-token-abc',
    email: 'john.smith@email.com',
    loanId: 'loan-001',
    borrowerId: 'borrower-001',
    firstName: 'John',
    lastName: 'Smith',
  },
  failed: {
    createPasswordToken: undefined,
    authToken: undefined,
    activationToken: undefined,
  },
};

export const b2cFeatureFlagScenarios: EndpointScenarioMap<B2CFeatureFlagResponse> = {
  default: {
    enabled: true,
    partnerId: 'partner-001',
  },
  disabled: {
    enabled: false,
    partnerId: 'partner-002',
  },
};

export const originationUserProfileScenarios: EndpointScenarioMap<OriginationUserProfile> = {
  default: {
    userId: 'user-001',
    email: 'john.smith@email.com',
    firstName: 'John',
    lastName: 'Smith',
    phone: '555-123-4567',
    preferredContactMethod: 'email',
    notificationPreferences: {
      email: true,
      sms: true,
      push: false,
      marketing: false,
    },
    createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
  },
  'new-user': {
    userId: 'user-002',
    email: 'new.user@email.com',
    firstName: 'New',
    lastName: 'User',
    createdAt: new Date().toISOString(),
  },
  'complete-profile': {
    userId: 'user-003',
    email: 'complete.user@email.com',
    firstName: 'Complete',
    lastName: 'User',
    phone: '555-987-6543',
    preferredContactMethod: 'phone',
    notificationPreferences: {
      email: true,
      sms: true,
      push: true,
      marketing: true,
    },
    createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
    updatedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
  },
};

export const updateProfileScenarios: EndpointScenarioMap<UpdateProfileResponse> = {
  default: {
    success: true,
    profile: {
      userId: 'user-001',
      email: 'john.smith@email.com',
      firstName: 'John',
      lastName: 'Smith',
      phone: '555-123-4567',
      preferredContactMethod: 'email',
      updatedAt: new Date().toISOString(),
    },
  },
  failed: {
    success: false,
    profile: {
      userId: 'user-001',
      email: 'john.smith@email.com',
      firstName: 'John',
      lastName: 'Smith',
    },
  },
};

export const userLoansScenarios: EndpointScenarioMap<UserLoan[]> = {
  default: [
    {
      loanId: 'loan-001',
      loanNumber: '1234567890',
      status: 'In Progress',
      loanType: 'Purchase',
      propertyAddress: '123 Main Street, Irvine, CA 92618',
    },
  ],
  'multiple-loans': [
    {
      loanId: 'loan-001',
      loanNumber: '1234567890',
      status: 'In Progress',
      loanType: 'Purchase',
      propertyAddress: '123 Main Street, Irvine, CA 92618',
    },
    {
      loanId: 'loan-002',
      loanNumber: '1234567891',
      status: 'Conditional Approval',
      loanType: 'Refinance',
      propertyAddress: '456 Oak Avenue, Costa Mesa, CA 92627',
    },
  ],
  'no-loans': [],
};

export const searchFinancialInstitutionsScenarios: EndpointScenarioMap<SearchFinancialInstitutionsResponse> = {
  default: {
    institutions: [
      { providerInstitutionId: 'fi-001', name: 'Chase Bank', logo: 'https://logo.clearbit.com/chase.com' },
      { providerInstitutionId: 'fi-002', name: 'Bank of America', logo: 'https://logo.clearbit.com/bankofamerica.com' },
      { providerInstitutionId: 'fi-003', name: 'Wells Fargo', logo: 'https://logo.clearbit.com/wellsfargo.com' },
    ],
    total: 3,
    hasMore: false,
  },
  'many-results': {
    institutions: [
      { providerInstitutionId: 'fi-001', name: 'Chase Bank', logo: 'https://logo.clearbit.com/chase.com' },
      { providerInstitutionId: 'fi-002', name: 'Bank of America', logo: 'https://logo.clearbit.com/bankofamerica.com' },
      { providerInstitutionId: 'fi-003', name: 'Wells Fargo', logo: 'https://logo.clearbit.com/wellsfargo.com' },
      { providerInstitutionId: 'fi-004', name: 'Citibank', logo: 'https://logo.clearbit.com/citi.com' },
      { providerInstitutionId: 'fi-005', name: 'US Bank', logo: 'https://logo.clearbit.com/usbank.com' },
      { providerInstitutionId: 'fi-006', name: 'PNC Bank', logo: 'https://logo.clearbit.com/pnc.com' },
      { providerInstitutionId: 'fi-007', name: 'Capital One', logo: 'https://logo.clearbit.com/capitalone.com' },
      { providerInstitutionId: 'fi-008', name: 'TD Bank', logo: 'https://logo.clearbit.com/td.com' },
    ],
    total: 8,
    hasMore: false,
  },
  'no-results': {
    institutions: [],
    total: 0,
    hasMore: false,
  },
};

export const getFinancialInstitutionScenarios: EndpointScenarioMap<GetFinancialInstitutionResponse> = {
  default: {
    institution: {
      providerInstitutionId: 'fi-001',
      name: 'Chase Bank',
      logo: 'https://logo.clearbit.com/chase.com',
    },
  },
};

export const featuredInstitutionsScenarios: EndpointScenarioMap<FinancialInstitution[]> = {
  default: [
    { providerInstitutionId: 'fi-001', name: 'Chase Bank', logo: 'https://logo.clearbit.com/chase.com' },
    { providerInstitutionId: 'fi-002', name: 'Bank of America', logo: 'https://logo.clearbit.com/bankofamerica.com' },
    { providerInstitutionId: 'fi-003', name: 'Wells Fargo', logo: 'https://logo.clearbit.com/wellsfargo.com' },
    { providerInstitutionId: 'fi-004', name: 'Citibank', logo: 'https://logo.clearbit.com/citi.com' },
    { providerInstitutionId: 'fi-007', name: 'Capital One', logo: 'https://logo.clearbit.com/capitalone.com' },
  ],
};
